/**
 * Фразы Wordstat тематики: «Запросы со словами» и «Похожие запросы». Отсюда
 * рождаются новые типы — кнопкой «сделать типом» у фразы.
 *
 * Фраза, которая уже тип, помечена и ведёт на него: второй раз тот же тип не
 * заводят (сервис вернул бы прежний, а человек решил бы, что ничего не вышло).
 * Отброшенный тип помечен отдельно — «сделать типом» его вернёт.
 *
 * В подборке тип зовётся словом: «в подборку» вместо «сделать типом». Чужую
 * подборку (`readOnly`) смотрят без кнопок — сервис всё равно ответил бы 403.
 */
import { useMutation } from "@tanstack/react-query";
import { Plus, Search, X } from "lucide-react";
import { useId, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router";

import { btn, btnGhost, btnPrimary, Empty, ErrorLine, input, label } from "@/components/trends/ui";
import { num } from "@/lib/media";
import { normPhrase, post, suggestTypeName, type Phrase, type Topic, type TrendType } from "@/lib/trends";

export function PhrasesPanel({
  topic,
  onTypeAdded,
  readOnly = false,
}: {
  topic: Topic;
  onTypeAdded: (name: string) => void;
  readOnly?: boolean;
}) {
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  const byPhrase = useMemo(() => new Map(topic.types.map((t) => [normPhrase(t.phrase), t] as const)), [topic.types]);
  const groups = useMemo(() => [...new Set(topic.types.map((t) => t.grp))].sort((a, b) => a.localeCompare(b, "ru")), [
    topic.types,
  ]);

  const needle = normPhrase(filter);
  const pick = (list: Phrase[]) => (needle ? list.filter((p) => normPhrase(p.text).includes(needle)) : list);

  const column = (title: string, hint: string, list: Phrase[], annot: string) => {
    const shown = pick(list);
    return (
      <div className="min-w-0" data-annot={annot}>
        <div className="flex items-baseline gap-2 border-b border-border bg-bg-subtle px-3 py-1.5">
          <span className="text-[12px] font-medium text-fg">{title}</span>
          <span className="text-[11px] text-fg-faint" title={hint}>
            {needle ? `${shown.length} из ${list.length}` : list.length}
          </span>
        </div>
        {list.length === 0 ? (
          <Empty>Пусто — соберите тематику из Wordstat.</Empty>
        ) : shown.length === 0 ? (
          <Empty>Ничего не нашлось.</Empty>
        ) : (
          <ul className="max-h-[520px] overflow-y-auto">
            {shown.map((p) => (
              <PhraseRow
                key={p.text}
                phrase={p}
                topic={topic}
                type={byPhrase.get(normPhrase(p.text))}
                groups={groups}
                readOnly={readOnly}
                open={open === `${annot}:${p.text}`}
                onOpen={(on) => setOpen(on ? `${annot}:${p.text}` : null)}
                onAdded={(name) => {
                  setOpen(null);
                  onTypeAdded(name);
                }}
              />
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <div className="relative">
          <Search size={13} className="pointer-events-none absolute top-1/2 left-2 -translate-y-1/2 text-fg-faint" />
          <input
            className={`${input} w-[260px] pl-7`}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="найти фразу"
          />
        </div>
        {readOnly ? null : (
          <span className="text-[11.5px] text-fg-faint">
            {topic.owner_user_id ? "Новое слово" : "Новый тип"} появится в таблице сразу, а его история — после
            «Обновить из Wordstat».
          </span>
        )}
      </div>
      <div className="grid divide-border lg:grid-cols-2 lg:divide-x">
        {column("Запросы со словами", "Фразы, содержащие затравку, с частотой за период таблицы", topic.phrases, "запросы со словами")}
        {column("Похожие запросы", "Ассоциации Wordstat: что ещё ищут те же люди", topic.associations, "похожие запросы")}
      </div>
    </div>
  );
}

function PhraseRow({
  phrase,
  topic,
  type,
  groups,
  readOnly,
  open,
  onOpen,
  onAdded,
}: {
  phrase: Phrase;
  topic: Topic;
  type: TrendType | undefined;
  groups: string[];
  readOnly: boolean;
  open: boolean;
  onOpen: (on: boolean) => void;
  onAdded: (name: string) => void;
}) {
  const isSeed = normPhrase(phrase.text) === normPhrase(topic.phrase ?? "");
  const activeType = type && type.state === "active" ? type : undefined;
  const noun = topic.owner_user_id ? "слово" : "тип";
  return (
    <li className="border-b border-border last:border-b-0">
      <div className="group flex items-center gap-2 px-3 py-1 text-[12.5px] hover:bg-bg-hover">
        <span className="min-w-0 flex-1 truncate" title={phrase.text}>
          {phrase.text}
        </span>
        {isSeed ? <span className="text-[11px] text-fg-faint">затравка</span> : null}
        {activeType ? (
          <Link
            to={`/trends/type/${activeType.id}`}
            className="whitespace-nowrap rounded-[4px] bg-accent-dim px-1.5 text-[11px] text-accent-text hover:underline"
            title={`Эта фраза уже ${noun}`}
          >
            {noun} · {activeType.name}
          </Link>
        ) : type ? (
          <span className="whitespace-nowrap rounded-[4px] bg-neutral-bg px-1.5 text-[11px] text-fg-faint" title={`${noun === "слово" ? "Слово отброшено" : "Тип отброшен"}; кнопка вернёт его`}>
            отброшен · {type.name}
          </span>
        ) : null}
        <span className="w-[72px] text-right tnum text-fg-muted">{num(phrase.count)}</span>
        {!activeType && !isSeed && !readOnly ? (
          <button
            type="button"
            className={`${btnGhost} min-w-[112px] justify-end ${open ? "" : "opacity-60 group-hover:opacity-100"}`}
            onClick={() => onOpen(!open)}
            title={noun === "слово" ? "Положить фразу словом в подборку" : "Сделать из фразы тип тематики"}
          >
            {open ? <X size={12} /> : <Plus size={12} />}
            {open ? "закрыть" : type ? "вернуть" : noun === "слово" ? "в подборку" : "сделать типом"}
          </button>
        ) : (
          <span className="h-7 min-w-[112px]" />
        )}
      </div>
      {open ? (
        <TypeForm
          phrase={phrase.text}
          topic={topic}
          groups={groups}
          initialName={type?.name ?? suggestTypeName(phrase.text, topic.phrase)}
          initialGroup={type?.grp ?? ""}
          onCancel={() => onOpen(false)}
          onAdded={onAdded}
        />
      ) : null}
    </li>
  );
}

function TypeForm({
  phrase,
  topic,
  groups,
  initialName,
  initialGroup,
  onCancel,
  onAdded,
}: {
  phrase: string;
  topic: Topic;
  groups: string[];
  initialName: string;
  initialGroup: string;
  onCancel: () => void;
  onAdded: (name: string) => void;
}) {
  const listId = useId();
  const [name, setName] = useState(initialName);
  const [group, setGroup] = useState(initialGroup);

  const add = useMutation({
    mutationFn: () =>
      post<{ id: number }>(`/api/trends/${topic.id}/types`, { phrase, name: name.trim(), group: group.trim() }),
    onSuccess: () => onAdded(name.trim() || phrase),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!add.isPending) add.mutate();
  };

  return (
    <form onSubmit={submit} className="grid gap-2 bg-bg-subtle px-3 py-2" data-annot="форма типа">
      <div className="text-[11.5px] text-fg-faint">
        Фраза: <span className="text-fg-muted">{phrase}</span>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="grid gap-1">
          <span className={label}>название</span>
          <input
            className={`${input} w-[180px]`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            autoFocus
            onKeyDown={(e) => e.key === "Escape" && onCancel()}
          />
        </label>
        <label className="grid gap-1">
          <span className={label}>группа</span>
          <input
            className={`${input} w-[150px]`}
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            list={listId}
            maxLength={60}
            placeholder="силуэт, детали…"
            onKeyDown={(e) => e.key === "Escape" && onCancel()}
          />
          <datalist id={listId}>
            {groups.map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>
        </label>
        <button type="submit" className={btnPrimary} disabled={add.isPending}>
          {add.isPending ? "Добавляю…" : topic.owner_user_id ? "Добавить слово" : "Добавить тип"}
        </button>
        <button type="button" className={btn} onClick={onCancel}>
          Отмена
        </button>
      </div>
      <ErrorLine error={add.error} />
    </form>
  );
}
